import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button, Card, Typography } from "@material-tailwind/react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useHttp } from "@/hooks/useFetch";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface InnerProps extends Props {
  http: ReturnType<typeof useHttp>["Http"];
}

interface State {
  hasError: boolean;
  error?: Error;
  retryCount: number;
}

class DynamicImportErrorBoundaryInner extends Component<InnerProps, State> {
  constructor(props: InnerProps) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): State {
    // 检查是否是动态导入失败
    const isDynamicImportError =
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Loading chunk") ||
      error.message.includes("Loading CSS chunk") ||
      error.name === "ChunkLoadError";

    return {
      hasError: true,
      error,
      retryCount: 0,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("动态导入错误:", error, errorInfo);

    // 如果是动态导入失败，尝试刷新页面
    if (this.isDynamicImportError(error)) {
      this.handleDynamicImportError();
    }
  }

  isDynamicImportError(error: Error): boolean {
    return (
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Loading chunk") ||
      error.message.includes("Loading CSS chunk") ||
      error.name === "ChunkLoadError"
    );
  }

  handleDynamicImportError = () => {
    const { retryCount } = this.state;
    const maxRetries = 3;

    if (retryCount < maxRetries) {
      // 增加重试次数
      this.setState((prevState) => ({ retryCount: prevState.retryCount + 1 }));

      // 延迟重试
      setTimeout(() => {
        window.location.reload();
      }, 1000 * (retryCount + 1));
    } else {
      // 超过最大重试次数，显示错误页面
      console.error("动态导入失败，已达到最大重试次数");
    }
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, retryCount: 0 });
    window.location.reload();
  };

  handleClearCache = () => {
    // 清除缓存并刷新
    if ("caches" in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    // 清除localStorage和sessionStorage中的相关缓存
    const keysToRemove = Object.keys(localStorage).filter(
      (key) =>
        key.includes("vite") || key.includes("chunk") || key.includes("module")
    );
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    window.location.reload();
  };

  handleReportError = async () => {
    const { error } = this.state;
    if (!error) return;

    try {
      const href =
        typeof window !== "undefined" ? window.location.href : "unknown";
      const payload = {
        message: error.message,
        stack: error.stack,
        pageUrl: href,
        type: this.isDynamicImportError(error) ? 0 : 0,
        status: 0,
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        // 下面这些字段后端有定义，但可以先不上报或让服务端补充：os、browser、fingerprint、remark
      };

      // 使用全局封装的 Http 实例上报错误（由外层组件注入）
      await this.props.http.post("/app/log/error/report", payload);
    } catch (e) {
      console.error("上报错误失败", e);
    }
  };

  render() {
    if (this.state.hasError) {
      const { error, retryCount } = this.state;
      const isDynamicImportError = this.isDynamicImportError(error!);
      const currentUrl =
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search}${window.location.hash}`
          : "unknown";

      if (isDynamicImportError && retryCount < 0) {
        return (
          <div className="h-screen w-screen flex flex-col justify-center items-center bg-gray-50 dark:bg-gray-900">
            <Card className="p-8 max-w-md w-full mx-4">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-700 mx-auto mb-4"></div>
                <Typography variant="h5" className="mb-2">
                  正在重新加载页面...
                </Typography>
                <Typography
                  variant="small"
                  className="text-gray-600 dark:text-gray-400"
                >
                  第 {retryCount + 1} 次尝试
                </Typography>
              </div>
            </Card>
          </div>
        );
      }

      return (
        <div className="h-screen w-screen flex flex-col justify-center items-center bg-gray-50 dark:bg-gray-900">
          <Card className="p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="text-red-500 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <Typography variant="h5" className="mb-2">
                页面加载失败
              </Typography>
              <Typography
                variant="small"
                className="text-gray-600 dark:text-gray-400 mb-6"
              >
                {isDynamicImportError
                  ? "应用已更新，需要重新加载页面"
                  : "加载页面时发生错误"}
              </Typography>

              <div className="mb-4 text-left">
                <Typography
                  variant="small"
                  className="font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  当前页面路径
                </Typography>
                <Typography
                  variant="small"
                  className="text-gray-600 dark:text-gray-400 break-all"
                >
                  {currentUrl}
                </Typography>
              </div>

              {error && (
                <div className="mb-6 text-left">
                  <Typography
                    variant="small"
                    className="font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    错误信息
                  </Typography>
                  <pre className="max-h-40 overflow-auto rounded-md bg-gray-100 dark:bg-gray-800 text-left text-xs text-red-600 dark:text-red-400 p-3 whitespace-pre-wrap break-all">
                    {error.message}
                    {error.stack && `\n\n${error.stack}`}
                  </pre>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={this.handleReportError}
                  color="blue"
                >
                  提交错误信息给后台
                </Button>

                <Button
                  fullWidth
                  onClick={this.handleRetry}
                  className="flex items-center justify-center gap-2"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  重新加载页面
                </Button>

                {isDynamicImportError && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={this.handleClearCache}
                    color="red"
                  >
                    清除缓存并重新加载
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export function DynamicImportErrorBoundary(props: Props) {
  const { Http } = useHttp();
  return <DynamicImportErrorBoundaryInner {...props} http={Http} />;
}
